import { flattenFoldersForPicker } from "@/lib/folders/folderTree";
import { AURELIA_OUTBOX_FOLDER, isOutboxNavFolder } from "@/lib/outbox/constants";
import {
  AURELIA_SNOOZED_FOLDER,
  SNOOZED_FOLDER_LABEL,
} from "@/lib/snooze/constants";
import { isSnoozedVirtualFolder } from "@/lib/snooze/snoozeNavFolder";
import { isUnifiedInboxNavFolder } from "@/lib/unified/constants";
import type { MailAccountSettings, PrimaryNavEntry } from "@/lib/mail/accountSettingsPrefs";
import {
  aureliaFolderPreferScore,
  findAureliaFolderForSlot,
  isAureliaImapNamespace,
} from "@/lib/mail/aureliaImapFolders";
import type { MailFolder } from "@/lib/types/mail";


const PROVIDER_NAV_SLOTS: NavFolderSlot[] = ["sent", "drafts", "trash", "junk"];

function providerJunkScore(folder: MailFolder): number {
  const upper = `${folder.name} ${folder.label}`.toUpperCase();
  if (/SPAM/.test(upper)) return 10;
  if (/JUNK|INDÉSIR|INDESIR/.test(upper)) return 15;
  return 40;
}

function providerSlotScore(slot: NavFolderSlot, folder: MailFolder): number {
  const upper = `${folder.name} ${folder.label}`.toUpperCase();
  if (upper.includes("[GMAIL]") || upper.includes("[GOOGLE MAIL]")) return 0;
  if (slot === "sent") return sentFolderAutoScore(folder);
  if (slot === "drafts") return draftsFolderAutoScore(folder);
  if (slot === "trash") return providerTrashScore(folder);
  if (slot === "junk") return providerJunkScore(folder);
  return 40;
}

/** Dossier spécial du fournisseur (Gmail, OVH, Outlook…), jamais un dossier Aurelia/. */
export function findProviderFolderForSlot(
  folders: MailFolder[],
  slot: NavFolderSlot
): MailFolder | null {
  let best: MailFolder | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const folder of folders) {
    if (isAureliaImapNamespace(folder.name)) continue;
    if (navFolderSlot(folder.name, folder.label) !== slot) continue;
    const score = providerSlotScore(slot, folder);
    if (score < bestScore) {
      best = folder;
      bestScore = score;
    }
  }
  return best;
}

function resolveProviderSlotFolder(
  flat: MailFolder[],
  slot: NavFolderSlot,
  overrides?: Partial<Record<NavFolderSlot, string>>
): MailFolder | null {
  const override = overrides?.[slot]?.trim();
  if (override && !isAureliaImapNamespace(override)) {
    const chosen = flat.find((folder) => folder.name === override);
    if (chosen && navFolderSlot(chosen.name, chosen.label) === slot) return chosen;
  }
  const provider = findProviderFolderForSlot(flat, slot);
  if (provider) return provider;
  return findAureliaFolderForSlot(flat, slot);
}

export function resolveDraftsImapFolder(
  folders: MailFolder[],
  settings?: Pick<MailAccountSettings, "folderSlotOverrides">
): string | null {
  const flat = flattenFoldersForPicker(folders);
  return resolveProviderSlotFolder(flat, "drafts", settings?.folderSlotOverrides)?.name ?? null;
}

/** Dossier IMAP « Envoyés » du compte (Gmail, OVH…). Aurelia/ seulement s'il n'y en a aucun. */
export function resolveSentImapFolder(
  folders: MailFolder[],
  settings?: Pick<MailAccountSettings, "folderSlotOverrides">
): string | null {
  const flat = flattenFoldersForPicker(folders);
  return resolveProviderSlotFolder(flat, "sent", settings?.folderSlotOverrides)?.name ?? null;
}

function providerTrashScore(folder: MailFolder): number {
  const upper = `${folder.name} ${folder.label}`.toUpperCase();
  if (/(?:^|[./])TRASH(?:$|[./])/i.test(folder.name)) return 10;
  if (/CORBEILLE|BIN|LIXEIRA|PAPIERKORB|CESTINO|PAPELERA/.test(upper)) return 15;
  if (/DELETED|SUPPRIM/.test(upper)) return 20;
  return 40;
}

export function resolveTrashImapFolder(
  folders: MailFolder[],
  settings?: Pick<MailAccountSettings, "folderSlotOverrides">
): string | null {
  const flat = flattenFoldersForPicker(folders);
  return resolveProviderSlotFolder(flat, "trash", settings?.folderSlotOverrides)?.name ?? null;
}

export function resolveJunkImapFolder(
  folders: MailFolder[],
  settings?: Pick<MailAccountSettings, "folderSlotOverrides">
): string | null {
  const flat = flattenFoldersForPicker(folders);
  return resolveProviderSlotFolder(flat, "junk", settings?.folderSlotOverrides)?.name ?? null;
}

/** Dossiers affichés en tête de navigation, dans cet ordre fixe par défaut. */
export type NavFolderSlot =
  | "inbox"
  | "outbox"
  | "snoozed"
  | "drafts"
  | "sent"
  | "trash"
  | "junk";

const NAV_FOLDER_ORDER: NavFolderSlot[] = [
  "inbox",
  "outbox",
  "snoozed",
  "drafts",
  "sent",
  "trash",
  "junk",
];

const NAV_FOLDER_LABELS: Record<NavFolderSlot, string> = {
  inbox: "Boîte de réception",
  outbox: "Envoi programmé",
  snoozed: "En attente",
  drafts: "Brouillons",
  sent: "Envoyés",
  trash: "Corbeille",
  junk: "Indésirables",
};

/** Nav / titre : sauvegarde brouillon IMAP en cours (comme « En attente d'envoi »). */
export const DRAFTS_SAVING_FOLDER_LABEL = "Brouillons en cours";

export function navFolderSlot(name: string, label: string): NavFolderSlot | null {
  const upperName = name.toUpperCase();
  const key = `${name} ${label}`.toUpperCase();

  if (upperName === "INBOX") return "inbox";

  if (/SNOOZ|DEFER|REMIND|\bREPORT/i.test(key)) return "snoozed";
  if (/ATTENTE/i.test(key) && !/ENVOI|SEND|OUTBOX|SMTP/i.test(key)) return "snoozed";

  if (/DRAFT|BROUILLON/i.test(key)) return "drafts";

  if (/TRASH|CORBEILLE|SUPPRIM/i.test(key)) return "trash";

  if (/JUNK|SPAM|INDÉSIR|INDESIR|COURRIER.?IND/i.test(key)) return "junk";

  if (/^INBOX[\.\/](SENT|ÉLÉMENTS.?ENVOY|ELEMENTS.?ENVOY)/i.test(name)) return "sent";
  if (/(^|[\.\/])SENT($|[\.\/])/i.test(name)) return "sent";
  if (/ENVOY/i.test(key) && !/INDÉSIR|INDESIR|SPAM|JUNK/i.test(key)) return "sent";

  return null;
}

function withNavLabel(folder: MailFolder, slot: NavFolderSlot): MailFolder {
  return { ...folder, label: NAV_FOLDER_LABELS[slot] };
}

export function defaultPrimaryNavOrder(): PrimaryNavEntry[] {
  return NAV_FOLDER_ORDER.map((slot) => ({ type: "slot", slot }));
}

function isValidNavSlotFolder(slot: NavFolderSlot, folder: MailFolder): boolean {
  if (slot === "inbox") return true;
  return folder.name.toUpperCase() !== "INBOX";
}

function sentFolderAutoScore(folder: MailFolder): number {
  if (/^INBOX\.Sent$/i.test(folder.name)) return 0;
  if (/éléments\s+envoyés/i.test(folder.label)) return 10;
  if (/^INBOX[\.\/]Sent$/i.test(folder.name)) return 15;
  if (/SENT/i.test(folder.name)) return 20;
  return 50;
}

/** Aligné sur mail_imap_drafts_folder_score (PHP) — évite save dans Drafts pendant que l’UI montre INBOX.Drafts. */
function draftsFolderAutoScore(folder: MailFolder): number {
  if (/^INBOX\.Drafts$/i.test(folder.name)) return 0;
  if (/^INBOX[\.\/]Drafts?$/i.test(folder.name)) return 10;
  if (/brouillons?/i.test(folder.label) || /brouillons?/i.test(folder.name)) return 15;
  if (/DRAFTS?/i.test(folder.name)) return 20;
  return 50;
}

/** Meilleur dossier IMAP du rôle. Le dossier du compte gagne. Aurelia/ est le secours. */
export function pickBestFolderForSlot(
  slot: NavFolderSlot,
  folders: MailFolder[]
): MailFolder | null {
  const matches = folders.filter((folder) => navFolderSlot(folder.name, folder.label) === slot);
  if (matches.length === 0) return null;
  if (PROVIDER_NAV_SLOTS.includes(slot)) {
    const provider = findProviderFolderForSlot(matches, slot);
    if (provider) return provider;
  }
  const aurelia = matches.find((folder) => isAureliaImapNamespace(folder.name));
  if (aurelia) return aurelia;
  if (matches.length === 1) return matches[0];
  if (slot === "sent") {
    return [...matches].sort(
      (a, b) =>
        aureliaFolderPreferScore(a) + sentFolderAutoScore(a) - (aureliaFolderPreferScore(b) + sentFolderAutoScore(b))
    )[0] ?? null;
  }
  if (slot === "drafts") {
    return [...matches].sort(
      (a, b) =>
        aureliaFolderPreferScore(a) +
        draftsFolderAutoScore(a) -
        (aureliaFolderPreferScore(b) + draftsFolderAutoScore(b))
    )[0] ?? null;
  }
  return [...matches].sort((a, b) => aureliaFolderPreferScore(a) - aureliaFolderPreferScore(b))[0] ?? null;
}

function resolveSlotFolderMap(
  flat: MailFolder[],
  overrides: Partial<Record<NavFolderSlot, string>>
): Map<NavFolderSlot, MailFolder> {
  const autoMap = new Map<NavFolderSlot, MailFolder>();
  for (const slot of NAV_FOLDER_ORDER) {
    if (slot === "outbox") continue;
    const best = pickBestFolderForSlot(
      slot,
      flat.filter((folder) => navFolderSlot(folder.name, folder.label) === slot)
    );
    if (best) autoMap.set(slot, best);
  }

  const resolved = new Map<NavFolderSlot, MailFolder>();
  for (const slot of NAV_FOLDER_ORDER) {
    if (slot === "outbox") {
      resolved.set(slot, {
        name: AURELIA_OUTBOX_FOLDER,
        label: NAV_FOLDER_LABELS.outbox,
        depth: 0,
        unread: 0,
      });
      continue;
    }

    if (PROVIDER_NAV_SLOTS.includes(slot)) {
      const provider = resolveProviderSlotFolder(flat, slot, overrides);
      if (provider && isValidNavSlotFolder(slot, provider)) {
        resolved.set(slot, withNavLabel(provider, slot));
        continue;
      }
    }

    const aurelia = findAureliaFolderForSlot(flat, slot);
    if (aurelia && isValidNavSlotFolder(slot, aurelia)) {
      resolved.set(slot, withNavLabel(aurelia, slot));
      continue;
    }

    const overrideName = overrides[slot];
    if (overrideName) {
      const folder = flat.find((f) => f.name === overrideName);
      if (folder && isValidNavSlotFolder(slot, folder)) {
        resolved.set(slot, withNavLabel(folder, slot));
        continue;
      }
    }
    const auto = autoMap.get(slot);
    if (auto) {
      resolved.set(slot, withNavLabel(auto, slot));
    } else if (slot === "snoozed") {
      resolved.set(slot, {
        name: AURELIA_SNOOZED_FOLDER,
        label: SNOOZED_FOLDER_LABEL,
        depth: 0,
        unread: 0,
      });
    }
  }

  return resolved;
}

/** Dossiers principaux vs le reste — affichage local, sans impact IMAP. */
export function splitNavFolders(
  folders: MailFolder[],
  options?: {
    hiddenSlots?: ReadonlySet<NavFolderSlot>;
    showExtraFolders?: boolean;
    folderSlotOverrides?: Partial<Record<NavFolderSlot, string>>;
    primaryNavOrder?: PrimaryNavEntry[];
    demotedNavSlots?: ReadonlySet<NavFolderSlot>;
  }
): {
  primary: MailFolder[];
  others: MailFolder[];
} {
  const hiddenSlots = options?.hiddenSlots ?? new Set<NavFolderSlot>();
  const showExtraFolders = options?.showExtraFolders !== false;
  const demotedSlots = options?.demotedNavSlots ?? new Set<NavFolderSlot>();
  const overrides = options?.folderSlotOverrides ?? {};
  const navOrder =
    options?.primaryNavOrder && options.primaryNavOrder.length > 0
      ? options.primaryNavOrder
      : defaultPrimaryNavOrder();

  const flat = flattenFoldersForPicker(folders);
  const flatByName = new Map(flat.map((f) => [f.name, f]));
  const slotFolders = resolveSlotFolderMap(flat, overrides);

  const primary: MailFolder[] = [];
  const primaryNames = new Set<string>();

  for (const entry of navOrder) {
    if (entry.type === "slot") {
      if (hiddenSlots.has(entry.slot) || demotedSlots.has(entry.slot)) continue;
      const folder = slotFolders.get(entry.slot);
      if (folder && !primaryNames.has(folder.name)) {
        primary.push(folder);
        primaryNames.add(folder.name);
      }
      continue;
    }

    const folder = flatByName.get(entry.name);
    if (folder && !primaryNames.has(folder.name)) {
      primary.push(folder);
      primaryNames.add(folder.name);
    }
  }

  const others = showExtraFolders ? flat.filter((f) => !primaryNames.has(f.name)) : [];

  return { primary, others };
}

export function buildNavLayoutFromSettings(
  folders: MailFolder[],
  settings?: Pick<
    MailAccountSettings,
    "hiddenNavSlots" | "showExtraFolders" | "folderSlotOverrides" | "primaryNavOrder" | "demotedNavSlots"
  >
) {
  return splitNavFolders(folders, {
    hiddenSlots: new Set(settings?.hiddenNavSlots ?? []),
    showExtraFolders: settings?.showExtraFolders !== false,
    folderSlotOverrides: settings?.folderSlotOverrides ?? {},
    primaryNavOrder: settings?.primaryNavOrder,
    demotedNavSlots: new Set(settings?.demotedNavSlots ?? []),
  });
}

/** Nav mobile — expose toujours corbeille / envoyés / brouillons (ignore masquage desktop). */
export function buildMobileNavLayoutFromSettings(
  folders: MailFolder[],
  settings?: Pick<MailAccountSettings, "folderSlotOverrides" | "primaryNavOrder">
) {
  return splitNavFolders(folders, {
    hiddenSlots: new Set<NavFolderSlot>(),
    demotedNavSlots: new Set<NavFolderSlot>(),
    showExtraFolders: true,
    folderSlotOverrides: settings?.folderSlotOverrides ?? {},
    primaryNavOrder:
      settings?.primaryNavOrder && settings.primaryNavOrder.length > 0
        ? settings.primaryNavOrder
        : defaultPrimaryNavOrder(),
  });
}

export { NAV_FOLDER_LABELS, NAV_FOLDER_ORDER };

export function isDraftsNavFolder(name: string, label: string): boolean {
  return navFolderSlot(name, label) === "drafts";
}

/** Dossier personnalisé — renommable / supprimable côté IMAP. */
export function isUserManagedImapFolder(name: string, label: string): boolean {
  if (name.toUpperCase() === "INBOX") return false;
  if (
    isOutboxNavFolder(name) ||
    isSnoozedVirtualFolder(name) ||
    isUnifiedInboxNavFolder(name)
  ) {
    return false;
  }
  return navFolderSlot(name, label) === null;
}

export function imapFolderLeafName(name: string): string {
  if (name.includes(".")) {
    const parts = name.split(".");
    return parts[parts.length - 1] ?? name;
  }
  if (name.includes("/")) {
    const parts = name.split("/");
    return parts[parts.length - 1] ?? name;
  }
  return name;
}
